import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, FileStack, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const POLL_INTERVAL_MS = 2000;
const HEARTBEAT_STALE_SECONDS = 60;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function secondsSince(isoString) {
  if (!isoString) return null;
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 1000));
}

// Pairs each Preview key with its Apply counterpart -- see
// services/gmail_statement_backfill.py's docstring for the outcome shapes.
const BUCKET_LABELS = [
  ['created', 'new', 'Ingested (new)'],
  // Apply-only: document-level count, distinct from the email-level
  // "created" row above -- one email/PDF can fan out into statements for
  // several properties (routes.management_statements.
  // ingest_gmail_statement_message). No previewKey: Preview never parses,
  // so it cannot know the per-property count, only the candidate-email one.
  ['statements_created', null, 'Statement docs created'],
  ['already_present', 'already_present', 'Already present'],
  ['not_matching_rule', 'not_matching_rule', 'Not matching rule'],
  ['no_pdf', 'no_pdf', 'No PDF found'],
  // parse_error also covers "agency_not_yet_onboarded" (stage field on the
  // sample row) -- a recognised agency with no verified parser yet, PDF
  // filed for manual onboarding rather than guessed at.
  ['parse_error', 'parse_error', 'Parse error'],
  ['fetch_error', 'fetch_error', 'Fetch error'],
  ['rerouted_from_invoices', 'would_reroute_from_invoices', 'Rerouted from invoices'],
  ['unjunked_and_rerouted', 'would_unjunk_and_reroute', 'Un-junked and rerouted'],
  ['stale_location', 'stale_location', 'Stale location'],
];

// Mirrors GmailAmountReextractionPanel.jsx's useJobPolling.
function useJobPolling({ onCompleted, onFailed, onProgress }) {
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((jobId) => {
    stop();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.statementBackfillJobStatus(jobId);
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error, data.progress || null);
        } else if (data.status === 'running') {
          onProgress(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, onCompleted, onFailed, onProgress]);

  return { polling, elapsed, start };
}

// reroute-cohort-exclusive outcomes (rerouted_from_invoices,
// would_reroute_from_invoices, unjunked_and_rerouted,
// would_unjunk_and_reroute, stale_location) are tallied separately but
// their samples land in one of two flat lists -- "reroute_samples" (mis-
// ingested-as-invoice cohort) or "junk_reroute_samples" (mis-junked
// cohort) -- tagged per-entry by "outcome". stale_location can come from
// EITHER cohort, so it reads from both. Everything else is bucketed
// directly under samples[outcome]. See
// services/gmail_statement_backfill.py::_run_chunk.
const _REROUTE_OUTCOMES = new Set([
  'rerouted_from_invoices', 'would_reroute_from_invoices',
  'unjunked_and_rerouted', 'would_unjunk_and_reroute', 'stale_location',
]);

function bucketSamples(samples, key) {
  if (_REROUTE_OUTCOMES.has(key)) {
    const fromInvoiceCohort = (samples?.reroute_samples || []).filter((s) => s.outcome === key);
    const fromJunkCohort = (samples?.junk_reroute_samples || []).filter((s) => s.outcome === key);
    return [...fromInvoiceCohort, ...fromJunkCohort];
  }
  return samples?.[key] || [];
}

function EvidenceTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-muted-foreground pl-4 py-1">No sample evidence recorded.</p>;
  }
  const columns = Object.keys(rows[0]).filter((c) => c !== 'outcome');
  return (
    <div className="overflow-x-auto pl-4">
      <table className="w-full text-xs border">
        <thead className="bg-muted/50">
          <tr>{columns.map((c) => <th key={c} className="text-left p-1">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t" data-testid="statement-backfill-evidence-row">
              {columns.map((c) => (
                <td key={c} className="p-1 font-mono break-all">{String(row[c] ?? '—').slice(0, 200)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChainSection({
  operation, title, icon, chain, progress, failed, polling, elapsed, starting, stopping,
  onRun, onStop, runLabel, runDisabled,
}) {
  const running = chain?.status === 'running';
  const live = progress || chain?.seed;
  const cohortCount = chain?.metadata?.cohort_count;
  const processed = live?.cohort_processed;
  const heartbeatAge = secondsSince(chain?.heartbeat_at);
  const stalled = running && !polling && heartbeatAge != null && heartbeatAge > HEARTBEAT_STALE_SECONDS;
  const tallies = live?.tallies || {};
  const byMonth = live?.by_month || {};
  const samples = live?.samples || {};
  const errorText = (chain?.status === 'error' && chain?.error) || failed;
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleBucket = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium flex items-center gap-1">{icon}{title}</span>
        <Button data-testid={`statement-backfill-${operation}-run`} onClick={onRun} disabled={runDisabled} size="sm">
          {(starting || polling) ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
          {running
            ? `Chain running, run ${fmtInt(chain.runs_count)}, ${fmtInt(processed)}/${fmtInt(cohortCount)}`
            : runLabel}
        </Button>
        {running && (
          <Button data-testid={`statement-backfill-${operation}-stop`} onClick={onStop} disabled={stopping} size="sm" variant="outline">
            {stopping ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Stop Chain
          </Button>
        )}
        {polling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            {elapsed}s
          </span>
        )}
      </div>

      {chain && (
        <div className="text-xs space-y-1" data-testid={`statement-backfill-${operation}-chain-summary`}>
          <div className="flex items-center gap-1">
            <Badge variant={running ? 'default' : chain.status === 'error' ? 'destructive' : 'outline'}>
              {chain.status}
            </Badge>
            <span className="text-muted-foreground">
              run {fmtInt(chain.runs_count)}
              {cohortCount != null && <> -- {fmtInt(processed)} of {fmtInt(cohortCount)} processed</>}
            </span>
            {stalled && (
              <span className="text-amber-600 dark:text-amber-400">
                last active {heartbeatAge}s ago -- watchdog will resume it
              </span>
            )}
          </div>

          {Object.keys(tallies).length > 0 && (
            <div className="space-y-1" data-testid={`statement-backfill-${operation}-tallies`}>
              {BUCKET_LABELS.map(([applyKey, previewKey, label]) => {
                const key = operation === 'apply' ? applyKey : previewKey;
                const count = tallies[key];
                if (!count) return null;
                const isOpen = expanded.has(key);
                const rows = bucketSamples(samples, key);
                return (
                  <div key={key}>
                    <button
                      type="button"
                      onClick={() => toggleBucket(key)}
                      className={`flex items-center gap-1 hover:underline ${key === 'fetch_error' || key === 'parse_error' || key === 'stale_location' ? 'text-red-600' : ''}`}
                      data-testid={`statement-backfill-${operation}-bucket-${key}`}
                    >
                      {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      {label}: <strong>{count}</strong>
                    </button>
                    {isOpen && <EvidenceTable rows={rows} />}
                  </div>
                );
              })}
            </div>
          )}

          {Object.keys(byMonth).length > 0 && (
            <div>
              <span className="text-muted-foreground">
                {operation === 'apply'
                  ? 'Statement docs by month (statement_month):'
                  : 'Candidate emails by month (approximate -- Preview never parses):'}
              </span>
              <div
                className="flex flex-wrap gap-2"
                data-testid={`statement-backfill-${operation}-by-month`}
              >
                {Object.entries(byMonth).sort(([a], [b]) => a.localeCompare(b)).map(([month, count]) => (
                  <span key={month}>{month}: <strong>{count}</strong></span>
                ))}
              </div>
            </div>
          )}

          {errorText && <p className="text-red-600 dark:text-red-400">{errorText}</p>}
        </div>
      )}
    </div>
  );
}

export function ManagementStatementBackfillPanel() {
  const [previewChain, setPreviewChain] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewStopping, setPreviewStopping] = useState(false);

  const [applyChain, setApplyChain] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyStopping, setApplyStopping] = useState(false);

  const fetchPreviewChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.statementBackfillPreviewChainStatus();
      setPreviewChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const fetchApplyChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.statementBackfillApplyChainStatus();
      setApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const previewPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      fetchPreviewChain();
      toast.success(result?.truncated ? 'Preview run complete -- continuing chain' : 'Preview complete');
    }, [fetchPreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      fetchPreviewChain();
      toast.error(error ?? 'Preview run failed');
    }, [fetchPreviewChain]),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      fetchApplyChain();
      toast.success(result?.truncated ? 'Apply run complete -- continuing chain' : 'Apply complete');
    }, [fetchApplyChain]),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      fetchApplyChain();
      toast.error(error ?? 'Apply run failed');
    }, [fetchApplyChain]),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.statementBackfillJobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.statementBackfillJobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPreviewChain(); }, [fetchPreviewChain]);
  useEffect(() => { fetchApplyChain(); }, [fetchApplyChain]);

  useEffect(() => {
    if (previewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchPreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [previewChain?.status, fetchPreviewChain]);

  useEffect(() => {
    if (applyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [applyChain?.status, fetchApplyChain]);

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.statementBackfillPreviewChainStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A preview chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStarting(false);
    }
  };

  const runApply = async () => {
    const cohortCount = previewChain?.metadata?.cohort_count;
    if (!window.confirm(
      `Ingest/reroute ${cohortCount ?? 'the'} management-statement candidate(s) found since Jan 2026? ` +
      'New mail is ingested as a statement; mail mis-filed as a supplier invoice or mis-excluded as junk is rerouted (soft-excluded/un-excluded, never deleted).'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.statementBackfillApplyChainStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An apply chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  const stopPreviewChain = async () => {
    if (!previewChain?._id) return;
    if (!window.confirm('Stop the running Preview chain? The current run finishes its time window normally.')) return;
    setPreviewStopping(true);
    try {
      await diagnosticsAPI.statementBackfillPreviewChainStop(previewChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStopping(false);
    }
  };

  const stopApplyChain = async () => {
    if (!applyChain?._id) return;
    if (!window.confirm('Stop the running Apply chain? The current run finishes its time window normally.')) return;
    setApplyStopping(true);
    try {
      await diagnosticsAPI.statementBackfillApplyChainStop(applyChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStopping(false);
    }
  };

  const previewComplete = previewChain?.status === 'complete';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <FileStack className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Management Statement Backfill</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Searches Gmail for revenue-side owner/management statements from any known
          agency (config.STATEMENT_AGENCIES -- currently CWC; registry is revenue-side
          only, never cost-side rent/tenant statements) sent since January 2026
          (sender + keyword/filename rule, not the "Management Statements" label), and
          re-scans Supplier Invoices for statements mis-ingested there and Gmail
          junk-cleanup for statements mis-excluded there, both before their respective
          routing-rule fixes. Preview never writes. Apply ingests new mail through the
          same live routing path, reroutes mis-ingested invoice docs, and un-junks
          mis-excluded ones (all soft exclusion, never a hard delete; an unreachable
          source message is counted as stale location, not force-rerouted). An agency
          with no verified parser yet is never junked or invoice-quarantined -- its PDF
          is filed and surfaces under Parse error for manual onboarding. Budget-boxed
          chains: each run time-boxes itself and schedules its own successor until the
          cohort converges. Apply is gated behind a completed Preview over the same
          cohort.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <ChainSection
          operation="preview"
          title="Preview"
          icon={<Eye className="h-3 w-3" />}
          chain={previewChain}
          progress={previewProgress}
          failed={previewFailed}
          polling={previewPoll.polling}
          elapsed={previewPoll.elapsed}
          starting={previewStarting}
          stopping={previewStopping}
          onRun={runPreview}
          onStop={stopPreviewChain}
          runLabel="Preview"
          runDisabled={previewStarting || previewPoll.polling || previewChain?.status === 'running'}
        />
        <ChainSection
          operation="apply"
          title="Apply"
          icon={<Play className="h-3 w-3" />}
          chain={applyChain}
          progress={applyProgress}
          failed={applyFailed}
          polling={applyPoll.polling}
          elapsed={applyPoll.elapsed}
          starting={applyStarting}
          stopping={applyStopping}
          onRun={runApply}
          onStop={stopApplyChain}
          runLabel={previewComplete ? `Apply (${fmtInt(previewChain?.metadata?.cohort_count)} candidates)` : 'Apply'}
          runDisabled={applyStarting || applyPoll.polling || applyChain?.status === 'running' || !previewComplete}
        />
        {!previewComplete && (
          <p className="text-xs text-muted-foreground">
            Apply is disabled until a Preview chain over the current cohort completes.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
